import { z } from 'zod';
import {
  ACCESS_CODE_PATTERN,
  PASSWORD_MAX_LENGTH,
  PASSWORD_MIN_LENGTH,
  REGISTRATION_NUMBER_PATTERN,
} from '../constants.js';
import { emailSchema, phoneSchema, postalCodeSchema, shortText, optionalShortText } from './common.js';

/**
 * Password policy. Length is the dominant factor, so the floor is high and the
 * composition rules are deliberately light. The legacy system issued every new
 * partner the password "12345678" and hashed it with unsalted MD5.
 */
export const passwordSchema = z
  .string()
  .min(PASSWORD_MIN_LENGTH, `Mindestens ${PASSWORD_MIN_LENGTH} Zeichen`)
  .max(PASSWORD_MAX_LENGTH, `Maximal ${PASSWORD_MAX_LENGTH} Zeichen`)
  .refine((v) => /[a-z]/.test(v) && /[A-Z]/.test(v), 'Groß- und Kleinbuchstaben erforderlich')
  .refine((v) => /\d/.test(v), 'Mindestens eine Ziffer erforderlich');

export const registrationNumberSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(REGISTRATION_NUMBER_PATTERN, 'Registrierungsnummer im Format A-BBBB-CCCCC-DD');

// --- Mobile app (Firebase-backed) -----------------------------------------

/**
 * The client sends only its Firebase ID token. The server verifies the
 * signature and reads the uid and email out of the verified claims — it never
 * takes a user id from the request body, which is how the legacy API let any
 * caller act as any user.
 */
export const mobileSessionSchema = z.object({
  idToken: z.string().min(20).max(4096),
  notificationToken: z.string().max(512).optional(),
});

/**
 * Shared access code for the partner portal.
 *
 * Replaces the legacy `checkPin.php`, where the value was the literal `4164` in
 * the source. The code now comes from the environment and is verified
 * server-side; this schema only checks the shape.
 */
export const partnerAccessCodeSchema = z.object({
  code: z
    .string()
    .trim()
    .regex(ACCESS_CODE_PATTERN, 'Bitte geben Sie den Zugangscode ein'),
});

export type PartnerAccessCodeInput = z.infer<typeof partnerAccessCodeSchema>;

// --- Portal login (partner + admin) ---------------------------------------

export const partnerLoginSchema = z.object({
  registrationNumber: registrationNumberSchema,
  password: z.string().min(1, 'Passwort erforderlich').max(PASSWORD_MAX_LENGTH),
});

export const adminLoginSchema = z.object({
  username: shortText(64),
  password: z.string().min(1, 'Passwort erforderlich').max(PASSWORD_MAX_LENGTH),
  totpCode: z
    .string()
    .trim()
    .regex(/^\d{6}$/, 'Sechsstelliger Code')
    .optional(),
});

export const refreshSchema = z.object({
  refreshToken: z.string().min(20).max(512).optional(),
});

export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1).max(PASSWORD_MAX_LENGTH),
    newPassword: passwordSchema,
    confirmPassword: z.string(),
  })
  .refine((v) => v.newPassword === v.confirmPassword, {
    message: 'Passwörter stimmen nicht überein',
    path: ['confirmPassword'],
  })
  .refine((v) => v.currentPassword !== v.newPassword, {
    message: 'Neues Passwort muss sich vom aktuellen unterscheiden',
    path: ['newPassword'],
  });

export const forgotPasswordSchema = z.object({
  email: emailSchema,
});

export const resetPasswordSchema = z
  .object({
    token: z.string().min(20).max(512),
    newPassword: passwordSchema,
    confirmPassword: z.string(),
  })
  .refine((v) => v.newPassword === v.confirmPassword, {
    message: 'Passwörter stimmen nicht überein',
    path: ['confirmPassword'],
  });

// --- Partner self-registration --------------------------------------------

export const partnerRegistrationSchema = z.object({
  firstName: shortText(80),
  lastName: shortText(80),
  companyName: optionalShortText(160),
  registrationNumber: registrationNumberSchema,
  email: emailSchema,
  phone: phoneSchema,
  alternatePhone: phoneSchema.optional(),
  streetNumber: shortText(120),
  postalCode: postalCodeSchema,
  address: shortText(255),
  city: optionalShortText(120),
  latitude: z.coerce.number().min(-90).max(90).optional(),
  longitude: z.coerce.number().min(-180).max(180).optional(),
  password: passwordSchema,
  /** Document ids from a prior authenticated upload, not inline base64. */
  documentAssetIds: z.array(z.string().uuid()).min(1, 'Mindestens ein Dokument erforderlich').max(8),
  acceptedTerms: z.literal(true, {
    errorMap: () => ({ message: 'Bitte akzeptieren Sie die Bedingungen' }),
  }),
});

export type PartnerRegistrationInput = z.infer<typeof partnerRegistrationSchema>;
export type PartnerLoginInput = z.infer<typeof partnerLoginSchema>;
export type AdminLoginInput = z.infer<typeof adminLoginSchema>;
export type MobileSessionInput = z.infer<typeof mobileSessionSchema>;
